import { useState } from 'react';
import { CalendarClock, Check, ChevronDown } from 'lucide-react';
import { SkeletonLine } from 'components/skeleton/Skeleton';
import Tag from 'components/crm/ui/Tag';
import CallListBanner from 'components/prospection/CallListBanner';
import CallListStates from 'components/prospection/CallListStates';
import CallerLineHelp from 'components/prospection/CallerLineHelp';
import CallRow, { CompanyCell } from 'components/prospection/CallRow';
import SectionHeader from 'components/prospection/SectionHeader';
import useDragReorder from 'hooks/useDragReorder';
import useText from 'hooks/useText';
import { dayLabel, hourLabel } from 'helper/timeLabels';
import { dayState, splitLines } from 'pages/prospection/callListItems';

const RESULT_TONES = {
  answered: 'success',
  callback: 'orange',
  no_answer: 'neutral',
  not_interested: 'danger',
};

// Sticky header of the planned and queue blocks: title in ink, count in muted.
const BlockHeader = ({ title, count, children }) => (
  <div className="sticky top-0 z-10 flex h-12 items-center gap-3 bg-white">
    <span className="flex-1 font-title text-sm font-medium text-ink">
      {title} <span className="text-muted">· {count}</span>
    </span>
    {children}
  </div>
);

// A call made today, with the tag of its result; the cells are faded. It
// lines up with the rows to call: a tick where they have their rank.
const DoneRow = ({ call, text }) => (
  <div
    data-testid="done-row"
    className="flex h-16 items-center gap-3 border-b border-line"
  >
    <span className="flex size-7 shrink-0 items-center justify-center rounded-full border border-line bg-white text-faint">
      <Check size={14} />
    </span>
    <CompanyCell company={call.company} text={text} faded />
    <span className="flex shrink-0 items-center opacity-50">
      <Tag tone={RESULT_TONES[call.result] || 'neutral'} title>
        {text(`results.${call.result}`)}
      </Tag>
    </span>
  </div>
);

// A callback planned for a later day: when, in place of a result.
const UpcomingRow = ({ callback, text }) => (
  <div
    data-testid="upcoming-row"
    className="flex h-16 items-center gap-3 border-b border-line"
  >
    <span className="flex size-7 shrink-0 items-center justify-center rounded-full border border-line bg-white text-faint">
      <CalendarClock size={14} />
    </span>
    <CompanyCell company={callback.company} text={text} />
    <span className="flex shrink-0 items-center">
      <Tag tone="orange" title>
        {`${dayLabel(new Date(callback.dueAt))} · ${hourLabel(new Date(callback.dueAt))}`}
      </Tag>
    </span>
  </div>
);

// A part of the list that folds (« Terminés », « Relances planifiées »), closed at first.
const FoldedSection = ({ title, rows, children }) => {
  const [open, setOpen] = useState(false);
  if (!rows.length) return null;
  return (
    <section>
      <SectionHeader
        title={title}
        count={rows.length}
        expanded={open}
        onClick={() => setOpen((shown) => !shown)}
      >
        <span
          data-testid="fold-chevron"
          className={`flex transition-transform ${open ? '' : '-rotate-90'}`}
        >
          <ChevronDown size={20} />
        </span>
      </SectionHeader>
      {open && children}
    </section>
  );
};

/**
 * The left column of the prospection page: the goals banner, then the planned
 * calls, the queue and the calls made today, or the empty and finished
 * states. `simple`: the list alone, without the banner nor those states (the
 * page offers its prospects right under it); then its calls made today, its
 * callbacks planned for later days (`upcoming`: { id, company, dueAt }) and,
 * on the line of « À appeler », `queueActions`, and beside it the agency
 * numbers to show prospects (`callerLines`, CallerLineHelp). `onMoveQueue(from, to)`: the
 * queue's rows move by dragging them. Presentational: the page owns the data
 * and every action.
 */
const CallList = ({
  day,
  loading,
  error,
  now,
  selectedCompanyId,
  openCompanyId,
  queueActions,
  onRemove,
  onOpenGlobal,
  onAddProspect,
  onMoveQueue,
  upcoming = [],
  callerLines = [],
  simple = false,
}) => {
  const { text } = useText('prospection');
  const { rowProps, draggingIndex } = useDragReorder((from, to) =>
    onMoveQueue?.(from, to),
  );

  // The list to come, in its shapes: the goals banner, then rows of calls.
  if (loading || (!day && !error)) {
    return (
      <div data-testid="call-list-loading" aria-busy="true" className="flex flex-col gap-2">
        <div className="mb-4 flex items-center gap-4 rounded-2xl bg-gray-50 p-4">
          <div className="flex min-w-0 flex-1 flex-col gap-2">
            <SkeletonLine className="h-5 w-48" />
            <SkeletonLine className="h-3.5 w-64" />
          </div>
          {[0, 1].map((ring) => (
            <div key={ring} className="flex flex-col items-center gap-1">
              <span aria-hidden="true" className="size-12 animate-pulse rounded-full bg-gray-200" />
              <SkeletonLine className="h-3 w-12" />
            </div>
          ))}
        </div>
        <div>
          {Array.from({ length: 6 }, (_, index) => (
            <div key={index} className="flex h-16 items-center gap-3 border-b border-line last:border-b-0">
              <span aria-hidden="true" className="size-7 shrink-0 animate-pulse rounded-full bg-gray-200" />
              <span aria-hidden="true" className="size-8 shrink-0 animate-pulse rounded-md bg-gray-200" />
              <div className="flex min-w-0 flex-1 flex-col gap-1.5">
                <SkeletonLine className={index % 2 ? 'w-36' : 'w-44'} />
                <SkeletonLine className={`h-3 ${index % 2 ? 'w-24' : 'w-28'}`} />
              </div>
              <SkeletonLine className="h-6 w-20 rounded-md" />
            </div>
          ))}
        </div>
      </div>
    );
  }
  if (error) {
    return (
      <p role="alert" className="p-4 text-sm leading-5 text-danger">
        {text('states.loadError', { error })}
      </p>
    );
  }

  const state = dayState(day);
  const { planned, queue } = splitLines(day.pending);
  // Most recent first, whatever order the calls came in.
  const made = [...(day.made || [])].sort((a, b) =>
    a.at < b.at ? 1 : a.at > b.at ? -1 : 0,
  );
  // A call made today and planned again for a later day shows once, under
  // « Relances planifiées », where the list shows them (`simple`).
  const replanned = new Set(simple ? upcoming.map((callback) => callback.company?.id).filter(Boolean) : []);
  const done = made.filter((call) => !replanned.has(call.company?.id));
  // In the page's own list (power dial), a row says where it stands instead
  // of its attempt: the call open « En cours », the one after it « Suivant ».
  const nextLine = [...planned, ...queue].find(
    (line) => line.company?.id !== openCompanyId,
  );
  // Only while a call is under way: before « Lancer », no row says anything.
  const statusOf = (line) => {
    if (!simple) return undefined;
    if (!openCompanyId) return null;
    if (line.company?.id === openCompanyId) return 'current';
    return line === nextLine ? 'next' : null;
  };
  // `queueIndex`: its place in the queue, which may be dragged.
  const rowOf = (line, rank, queueIndex = null) => {
    const companyId = line.company && line.company.id;
    const movable = onMoveQueue && queueIndex !== null;
    return (
      <CallRow
        key={line.id}
        line={line}
        rank={rank}
        now={now}
        dragProps={movable ? rowProps(queueIndex) : undefined}
        dragging={movable && draggingIndex === queueIndex}
        status={statusOf(line)}
        selected={!!selectedCompanyId && companyId === selectedCompanyId}
        removeDisabled={!!openCompanyId && companyId === openCompanyId}
        onRemove={onRemove}
      />
    );
  };

  return (
    <div
      className={
        simple
          ? 'flex flex-col'
          : 'flex h-full min-h-0 w-[520px] shrink-0 flex-col gap-4'
      }
    >
      {!simple && <CallListBanner day={day} editing={false} />}
      <div className={simple ? '' : 'min-h-0 flex-1 overflow-y-auto'}>
        <FoldedSection title={text('blocks.done')} rows={done}>
          {done.map((call) => (
            <DoneRow key={call.id} call={call} text={text} />
          ))}
        </FoldedSection>
        {simple && (
          <FoldedSection title="Relances planifiées" rows={upcoming}>
            {upcoming.map((callback) => (
              <UpcomingRow key={callback.id} callback={callback} text={text} />
            ))}
          </FoldedSection>
        )}
        {state === 'active' ? (
          <>
            {/* Under the calls made, those left, always open. */}
            {simple && (
              <SectionHeader
                title="À appeler"
                count={planned.length + queue.length}
                aside={<CallerLineHelp lines={callerLines} calls={[...planned, ...queue]} />}
              >
                {queueActions}
              </SectionHeader>
            )}
            {planned.length > 0 && (
              <section>
                {!simple && (
                  <BlockHeader
                    title={text('blocks.plannedTitle')}
                    count={planned.length}
                  />
                )}
                {planned.map((line) => rowOf(line, null))}
              </section>
            )}
            <section>
              {/* In the page's own list, « À appeler » already heads it. */}
              {!simple && (
                <BlockHeader
                  title={text('blocks.queueTitle')}
                  count={queue.length}
                >
                  {queueActions}
                </BlockHeader>
              )}
              {queue.length > 0 ? (
                queue.map((line, index) => rowOf(line, index + 1, index))
              ) : (
                <p className="py-4 text-sm leading-5 text-muted">
                  {text('blocks.queueEmpty')}
                </p>
              )}
            </section>
          </>
        ) : simple ? null : (
          <CallListStates
            state={state}
            made={made}
            onOpenGlobal={onOpenGlobal}
            onAddProspect={onAddProspect}
          />
        )}
      </div>
    </div>
  );
};

export default CallList;
